// Key assumptions for conversion from USD/m² to USD/W
export const CONVERSION_CONSTANTS = {
  // Exchange rate (as of October 8, 2025)
  usdToEur: 0.92, // EUR/USD exchange rate

  // Module specifications
  modulePower: 583, // Wp
  moduleEfficiency: 23.25, // % (updated)
  moduleSize: 2.583, // m²
  moduleWeight: 31.8, // Kg
  glassThickness: 2, // mm (rear and front)

  // Power density - calculated from module efficiency: 1000 W/m² * 23.25% = 232.5 Wp/m²
  powerPerM2: 232.5, // Wp/m² (updated based on 23.25% module efficiency)

  // Wafer specifications
  waferArea: 334, // cm²
  lifetime: 30, // years

  // Material consumption
  polysiliconConsumption: 0.595, // kg/m² wafer (updated)
  czIngotConsumption: 2.4, // g/Wp
  waferThickness: 130, // μm
  kerfLoss: 65, // μm
  otherLoss: 20, // μm
  totalThickness: 215, // μm (1 wafer + losses)

  // Cell specifications
  cellEfficiency: 25, // %
  cellArea: 0.0334, // m²
  cellNumber: 72, // pcs
  cellPackingFactor: 0.94, // % of cell area that fits in module (94%)

  // Power density at cell level - calculated from cell efficiency: 1000 W/m² * 25% = 250 Wp/m²
  cellPowerPerM2: 250, // Wp/m² (based on 25% cell efficiency)

  // Financial parameters
  wacc: 10, // %
  tax: 30, // %

  // Depreciation periods
  depreciationPolysilicon: [9, 12], // years [min, max]
  depreciationWaferIngot: [10, 13], // years [min, max]
  depreciationCell: 12, // years
  depreciationModule: 7, // years
};

/**
 * Convert USD/m² to USD/W
 * @param {number} pricePerM2 - Price in USD/m²
 * @returns {number} - Price in USD/W
 */
export const convertM2ToW = (pricePerM2) => {
  return pricePerM2 / CONVERSION_CONSTANTS.powerPerM2;
};

/**
 * Convert USD/W to USD/m²
 * @param {number} pricePerW - Price in USD/W
 * @returns {number} - Price in USD/m²
 */
export const convertWToM2 = (pricePerW) => {
  return pricePerW * CONVERSION_CONSTANTS.powerPerM2;
};

/**
 * Format price with both USD/m² and USD/W, including EUR conversion
 * @param {number} pricePerM2 - Price in USD/m²
 * @returns {object} - Object with formatted prices
 */
export const formatDualPrice = (pricePerM2) => {
  const pricePerW = convertM2ToW(pricePerM2);
  const eurPerM2 = pricePerM2 * CONVERSION_CONSTANTS.usdToEur;
  const eurPerW = pricePerW * CONVERSION_CONSTANTS.usdToEur;

  return {
    perM2: pricePerM2.toFixed(2),
    perW: pricePerW.toFixed(3),
    eurPerM2: eurPerM2.toFixed(2),
    eurPerW: eurPerW.toFixed(3),
    perM2Full: `$${pricePerM2.toFixed(2)}/m²`,
    perWFull: `$${pricePerW.toFixed(3)}/W`
  };
};

/**
 * Calculate module cost from cell cost
 * @param {number} cellCost - Cell cost in USD/m²
 * @returns {number} - Estimated module cost in USD/m²
 */
export const estimateModuleCost = (cellCost) => {
  // This is a simplified estimate - actual module cost includes assembly, glass, etc.
  // The actual values should come from your simulation data
  return cellCost * 1.45; // Rough multiplier, adjust based on your data
};

/**
 * Convert stage-specific prices from USD/m² to USD/W
 * Different stages have different conversion factors based on efficiency
 * @param {number} pricePerM2 - Price in USD/m²
 * @param {string} stage - Manufacturing stage ('polysilicon', 'ingotWafer', 'cell', 'module')
 * @returns {number} - Price in USD/W
 */
export const convertStageM2ToW = (pricePerM2, stage) => {
  switch (stage) {
    case 'polysilicon':
    case 'ingotWafer':
      // Polysilicon and Ingot/Wafer use cell efficiency (25%) for conversion
      return pricePerM2 / CONVERSION_CONSTANTS.cellPowerPerM2;
    case 'cell':
      // Cell uses cell efficiency (25%)
      return pricePerM2 / CONVERSION_CONSTANTS.cellPowerPerM2;
    case 'module':
      // Module uses module efficiency (23.25%)
      return pricePerM2 / CONVERSION_CONSTANTS.powerPerM2;
    default:
      return pricePerM2 / CONVERSION_CONSTANTS.powerPerM2;
  }
};

/**
 * Convert cumulative MSP from USD/m² to USD/W based on the stage
 * @param {number} cumulativePrice - Cumulative price in USD/m²
 * @param {string} stage - Manufacturing stage
 * @returns {number} - Price in USD/W
 */
export const convertCumulativeM2ToW = (cumulativePrice, stage) => {
  // For cumulative prices, use the stage's final efficiency
  return convertStageM2ToW(cumulativePrice, stage);
};
